import React, { useEffect, useRef, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    Box,
    Chip,
    IconButton,
    CircularProgress,
} from "@mui/material";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LinkIcon from "@mui/icons-material/Link";

import { Link } from "react-router-dom";
import {
    getLabReport,
    getCoc,
} from "../../services/reportService";

const columns = [
    "Order Number",
    "Sample Date",
    "Project",
    "Status",
    "Downloads",
];

const columnHeaders = [
    "orderNumber",
    "sampleDate",
    "project",
    "status",
    "downloads",
];

const LabOrdersTable = ({
    labOrdersData,
    count,
    onChange,
    loading,
}) => {
    const rowsPerPageOptions = [10, 25, 50];

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] =
        useState(10);

    const [orderBy, setOrderBy] =
        useState("orderNumber");
    const [order, setOrder] = useState("asc");

    const tableContainerRef = useRef(null);
    const [tableHeight, setTableHeight] =
        useState(460);

    useEffect(() => {
        onChange(page, rowsPerPage);
    }, [page, rowsPerPage]);

    useEffect(() => {
        const calculateTableHeight = () => {
            if (tableContainerRef.current) {
                const top =
                    tableContainerRef.current.getBoundingClientRect()
                        .top;

                const height =
                    window.innerHeight - top - 76;

                setTableHeight(Math.max(300, height));
            }
        };

        calculateTableHeight();

        window.addEventListener(
            "resize",
            calculateTableHeight
        );

        return () =>
            window.removeEventListener(
                "resize",
                calculateTableHeight
            );
    }, [labOrdersData]);

    const handleSort = (property) => {
        const isAsc =
            orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedData = [...labOrdersData].sort(
        (a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];

            if (aVal < bVal)
                return order === "asc" ? -1 : 1;

            if (aVal > bVal)
                return order === "asc" ? 1 : -1;

            return 0;
        }
    );

    return (
        <Paper>
            <Box sx={{ width: "100%" }}>
                <TableContainer
                    ref={tableContainerRef}
                    sx={{
                        maxHeight: tableHeight,
                        overflowY: "auto",
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((col, index) => (
                                    <TableCell
                                        key={col}
                                        sx={{
                                            backgroundColor: "primary.main",
                                            color: "primary.contrastText",
                                            fontWeight: 600,
                                        }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === columnHeaders[index]}
                                            direction={order}
                                            onClick={() =>
                                                handleSort(columnHeaders[index])
                                            }
                                            sx={{
                                                color: "inherit",
                                                "&.Mui-active": {
                                                    color: "inherit",
                                                },
                                                "& .MuiTableSortLabel-icon": {
                                                    color: "inherit !important",
                                                },
                                            }}
                                        >
                                            {col}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                    >
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedData.map((row) => (
                                    <TableRow
                                        key={row.orderNumber}
                                        hover
                                    >
                                        <TableCell>
                                            <Link
                                                to={`/order/${row.orderNumber}`}
                                            >
                                                {row.orderNumber}
                                            </Link>
                                        </TableCell>

                                        <TableCell>
                                            {new Date(
                                                row.sampleDate
                                            ).toLocaleDateString()}
                                        </TableCell>

                                        <TableCell>
                                            {row.project}
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={row.status}
                                                color={
                                                    row.status === "Paid"
                                                        ? "success"
                                                        : row.status ===
                                                            "Invoiced"
                                                            ? "warning"
                                                            : "primary"
                                                }
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell>
                                            {row.hasReport && (
                                                <IconButton
                                                    onClick={() =>
                                                        getLabReport(
                                                            row.orderNumber
                                                        )
                                                    }
                                                >
                                                    <PictureAsPdfIcon />
                                                </IconButton>
                                            )}

                                            {row.cocs?.map(
                                                (coc, index) => (
                                                    <IconButton
                                                        key={index}
                                                        onClick={() =>
                                                            getCoc(coc)
                                                        }
                                                    >
                                                        <LinkIcon />
                                                    </IconButton>
                                                )
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) =>
                    setPage(newPage)
                }
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(
                        parseInt(e.target.value, 10)
                    );
                    setPage(0);
                }}
            />
        </Paper>
    );
};

export default LabOrdersTable;